import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Package, Search } from 'lucide-react';
import { componentService } from '../../../services/componentService'; // Use main componentService
import { productService } from '../../../services/productService'; // Import productService
import { sellerProductService } from '../../../services/sellerProductService'; // NEW: Import sellerProductService
import { useAuth } from '../../../contexts/AuthContext'; // To get authenticated user ID
import ComponentModal from './ComponentModal'; // Re-use existing modal
import ComponentDetailModal from '../../model-catalog/components/ComponentDetailModal'; // NEW: Preview modal
import { formatPrice } from '../../../utils/formatters';
// No need for ratingService or Icon specific for ratings in Seller Dashboard initially
// import ratingService from '../../../services/ratingService';
import Icon from '../../../components/AppIcon';
import { imageUploadService } from '../../../utils/imageUploadService'; // NEW
import { supabase } from '../../../lib/supabase';
import SellerBundleManagement from './SellerBundleManagement';
import Switch from '../../../components/ui/Switch';

export default function SellerComponentManagement({ initialSelectedId, clearInitialSelectedId }) {
  const { user } = useAuth(); // Get authenticated user
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [sellerAccountId, setSellerAccountId] = useState(null);
  const [sellerProducts, setSellerProducts] = useState([]); // State to store products for the seller
  const [selectedProductId, setSelectedProductId] = useState(() => {
    // Initialize state from sessionStorage if available
    return sessionStorage.getItem('sellerComponentMgmt_selectedProductId') || '';
  });
  

  const [currentPage, setCurrentPage] = useState(1);
  const [layoutMode, setLayoutMode] = useState(() => localStorage.getItem('seller_preferred_layout') || 'list'); // Symmetrical dynamic initial loading
  const [isCustomOrderingActive, setIsCustomOrderingActive] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault(); // Required to allow dropping
  };
  
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (sourceIndex === targetIndex) return;
    
    setComponents(prev => {
      const list = [...prev];
      const [movedItem] = list.splice(sourceIndex, 1);
      list.splice(targetIndex, 0, movedItem);
      return list;
    });
  };

  const handleToggleCustomOrdering = async (checked) => {
    setIsCustomOrderingActive(checked);
    if (checked) {
      // Symmetrical: Reset any active column sorting filters to prevent them from fighting with drag-and-drop locations!
      setSortConfig({ key: null, direction: 'asc' });
    } else {
      setIsSavingOrder(true);
      try {
        const promises = components.map((comp, idx) => 
          supabase
            .from('seller_components')
            .update({ display_order: idx })
            .eq('id', comp.id)
        );
        await Promise.all(promises);

        // Symmetrical: Refetch the list from the database to ensure the state sequence is perfectly synchronized and locked!
        if (sellerAccountId && user?.id) {
          await loadComponents(sellerAccountId, user.id, selectedProductId);
        }
      } catch (err) {
        console.error('Error saving components display sequence:', err);
      } finally {
        setIsSavingOrder(false);
      }
    }
  };
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const itemsPerPage = layoutMode === 'grid' ? 12 : 10; // Dynamic items count prevents trailing empty slots on cards grid row!

  useEffect(() => {
    setCurrentPage(1);
  }, [localSearchQuery, selectedProductId, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };


  const handleToggleStatus = async (component) => {
    try {
      const newStatus = component.isActive ? 'inactive' : 'active';
      // Optimistic update
      setComponents(prev => prev.map(c => c.id === component.id ? { ...c, isActive: !component.isActive } : c));

      const submitData = {
        isActive: newStatus === 'active'
      };

      await componentService.update(component.id, submitData);
    } catch (err) {
      console.error('Error toggling component status:', err);
      // Revert on error
      setComponents(prev => prev.map(c => c.id === component.id ? { ...c, isActive: component.isActive } : c));
      setError(err.message || 'Failed to update status');
    }
  };
  const loadSellerProducts = async (sellerId) => {
    try {
      // Fetch products the seller is selling from seller_products
      const sellerProds = await sellerProductService.getSellerProductsBySellerId(sellerId);
      // Map to a unified format for the dropdown (id is productId, name is productName)
      const formattedProducts = sellerProds.map(sp => ({
        id: sp.productId,
        name: sp.productName || 'Unknown Product'
      }));
      setSellerProducts(formattedProducts);
    } catch (err) {
      console.error('Load seller products error:', err);
    }
  };

  // Effect to save selectedProductId to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('sellerComponentManagementSelectedProductId', selectedProductId);
  }, [selectedProductId]);

  useEffect(() => {
    const initializeDashboard = async () => {
      if (user?.id) {
        setLoading(true);
        try {
          // CRITICAL: Fetch the seller_account ID for this user
          const { data: sellerAccount, error: accError } = await supabase
            .from('seller_accounts')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (accError) throw accError;
          
          if (sellerAccount) {
            setSellerAccountId(sellerAccount.id);
            await Promise.all([
              loadComponents(sellerAccount.id, user.id, selectedProductId),
              loadSellerProducts(sellerAccount.id)
            ]);
          } else {
            setError('No seller account found for this user.');
            setLoading(false);
          }
        } catch (err) {
          console.error('Initialization error:', err);
          setError('Failed to initialize dashboard.');
          setLoading(false);
        }
      } else {
        setLoading(false);
        setError('You must be logged in to view your components.');
      }
    };

    initializeDashboard();
  }, [user?.id, selectedProductId]);

const loadComponents = async (sellerId, userId, productId = null) => {
  try {
    setLoading(true);
    setError('');
    // CRITICAL FIX: Passing both IDs for inclusive matching in service
    // includeInactive ensures that components don't disappear when toggled off
    const filters = { sellerId: sellerId, createdBy: userId, includeInactive: true };
    if (productId) {
      filters.productId = productId;
    }

    const data = await componentService.getAll(filters);
    setComponents(data || []);
    } catch (err) {
    console.error('Load seller components error:', err);
    setError(err?.message || 'Failed to load your components');
    setComponents([]);
    } finally {
    setLoading(false);
    }
    };

    const [displayComponents, setDisplayComponents] = useState([]);

    useEffect(() => {
      if (initialSelectedId && components.length > 0) {
        const matched = components.find(c => c.id === initialSelectedId);
        if (matched) {
          setSelectedComponent(matched);
          setShowModal(true);
          if (clearInitialSelectedId) {
            clearInitialSelectedId();
          }
        }
      }
    }, [initialSelectedId, components, clearInitialSelectedId]);

    useEffect(() => {
    const processComponentImageUrls = async () => {
    if (components?.length === 0) {
      setDisplayComponents([]);
      return;
    }

    const processed = components.map((comp) => {
      if (comp.images && comp.images.length > 0) {
        const publicUrls = comp.images.map((path) => {
          if (!path) return null;
          if (path.startsWith('http')) {
            return path;
          }
          return imageUploadService.getPublicUrl(path);
        });
        return {
          ...comp,
          previewImages: publicUrls.filter(url => url !== null), // Create previewImages instead of overwriting
          imageUrls: publicUrls.filter(url => url !== null) // Store public URLs
        };
      }
      return { ...comp, images: [], imageUrls: [] }; // Ensure imageUrls is always present
    });

    setDisplayComponents(processed);
    };

    processComponentImageUrls();
    }, [components]);

    // Apply local search dynamically
    const filteredComponents = displayComponents.filter(c => {
    if (!localSearchQuery) return true;
    const q = localSearchQuery.toLowerCase();
    return c?.name?.toLowerCase()?.includes(q) ||
         c?.customName?.toLowerCase()?.includes(q) ||
         c?.sku?.toLowerCase()?.includes(q) ||
         c?.manufacturerPartNumber?.toLowerCase()?.includes(q) ||
         c?.upc?.toLowerCase()?.includes(q);
    }); // Re-run when components state changes


  let sortedComponents = [...filteredComponents];
  if (sortConfig.key) {
    sortedComponents.sort((a, b) => {
      let valA, valB;
      switch (sortConfig.key) {
        case 'component': valA = a.customName || a.name || ''; valB = b.customName || b.name || ''; break;
        case 'condition': valA = a.condition || ''; valB = b.condition || ''; break;
        case 'sku': valA = a.sku || ''; valB = b.sku || ''; break;
        case 'upc': valA = a.productUPC || ''; valB = b.productUPC || ''; break;
        case 'manufacturer': valA = a.manufacturer?.name || ''; valB = b.manufacturer?.name || ''; break;
        case 'product': valA = a.product?.name || ''; valB = b.product?.name || ''; break;
        case 'price': valA = parseFloat(a.price || 0); valB = parseFloat(b.price || 0); break;
        case 'return': valA = parseInt(a.returnperiod || 0); valB = parseInt(b.returnperiod || 0); break;
        case 'stock': valA = parseInt(a.stockQuantity || 0); valB = parseInt(b.stockQuantity || 0); break;
        case 'views': valA = parseInt(a.viewsCount || 0); valB = parseInt(b.viewsCount || 0); break;
        case 'status': valA = a.isActive ? 1 : 0; valB = b.isActive ? 1 : 0; break;
        default: return 0;
      }
      
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const totalPages = Math.max(1, Math.ceil(sortedComponents.length / itemsPerPage));
  const paginatedComponents = sortedComponents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const displayComponentsList = isCustomOrderingActive ? sortedComponents : paginatedComponents;


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this component?')) return;
    
    try {
      await componentService?.delete(id);
      loadComponents(sellerAccountId, user?.id, selectedProductId); // Reload components for the current seller
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleEdit = (component) => {
    setSelectedComponent(component);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedComponent(null);
    setShowModal(true);
  };

  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setSelectedComponent(null);
    if (shouldRefresh) {
      loadComponents(sellerAccountId, user?.id, selectedProductId);
    }
  };

  if (loading && components.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={() => loadComponents(sellerAccountId, user?.id, selectedProductId)} className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SECTION 1: Your Components */}
      <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Your Offered Components ({filteredComponents?.length})</h2>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-border">
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {sellerProducts?.length > 0 && (
            <select
              value={selectedProductId}
              onChange={(e) => {
                setSelectedProductId(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium bg-white"
            >
              <option value="">All Products</option>
              {sellerProducts.map(product => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </select>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search your components..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e?.target?.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add New Component
          </button>
        </div>
      </div>

      {/* Symmetrical Drag and Drop Custom Sequencing Toggle Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-gray-50 border border-gray-200 p-4 rounded-xl justify-between text-left shadow-sm">
        <div className="flex items-center gap-2">
          <Switch 
            checked={isCustomOrderingActive}
            onCheckedChange={handleToggleCustomOrdering}
          />
          <div>
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Configure Custom Display Order</h4>
            <p className="text-[10px] text-gray-500">Enable this toggle to drag & drop items to customize their display order. Turning it off instantly saves the order.</p>
          </div>
        </div>

        {/* Symmetrical Layout Mode Switcher Buttons */}
        <div className="flex items-center gap-1.5 self-end sm:self-auto bg-white p-1 rounded-lg border border-slate-200">
          <button 
            type="button" 
            onClick={() => setLayoutMode('grid')} 
            className={`px-2.5 py-1 rounded text-xs font-bold flex items-center gap-1 transition-all ${layoutMode === 'grid' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Icon name="Grid" size={14} />
            Cards
          </button>
          <button 
            type="button" 
            onClick={() => setLayoutMode('list')} 
            className={`px-2.5 py-1 rounded text-xs font-bold flex items-center gap-1 transition-all ${layoutMode === 'list' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Icon name="List" size={14} />
            List
          </button>
        </div>

        {isSavingOrder && (
          <span className="text-[10px] font-bold text-blue-600 animate-pulse">Saving display sequence...</span>
        )}
      </div>

      {/* Symmetrical Components Layout Rendering */}
      {layoutMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mt-4">
          {loading ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-400 animate-pulse" />
              <p>Loading your components...</p>
            </div>
          ) : filteredComponents?.length === 0 ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No components found for your account</p>
            </div>
          ) : (
            displayComponentsList?.map((component, index) => {
              const imgUrl = component?.imageUrls?.[0] 
                ? (component.imageUrls[0].startsWith('http') || component.imageUrls[0].startsWith('/assets') || component.imageUrls[0].startsWith('/api') ? component.imageUrls[0] : imageUploadService.getPublicUrl(component.imageUrls[0], 'component-images'))
                : null;

              return (
                <div 
                  key={component?.id}
                  draggable={isCustomOrderingActive}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`flex flex-col relative bg-card overflow-hidden group hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-border rounded-lg text-left ${isCustomOrderingActive ? 'cursor-move ring-2 ring-blue-500/30 bg-blue-50/10' : ''}`}
                  onClick={() => {
                    if (!isCustomOrderingActive) {
                      setSelectedComponent(component);
                      setShowPreviewModal(true);
                    }
                  }}
                >
                  {/* Symmetrical Drag Handle or Index Badge */}
                  {isCustomOrderingActive ? (
                    <div className="absolute top-2 left-2 z-20 bg-blue-600 text-white rounded-lg p-1.5 flex items-center justify-center shadow-md cursor-move" onClick={(e) => e.stopPropagation()}>
                      <Icon name="GripVertical" className="w-3.5 h-3.5" />
                    </div>
                  ) : (
                    <div className="absolute top-2 left-2 z-20 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                      #{index + 1}
                    </div>
                  )}

                  {/* Thumbnail Image matching actual store catalog sizing and scaling */}
                  <div className="relative aspect-square overflow-hidden bg-muted flex items-center justify-center border-b border-border">
                    {imgUrl ? (
                      <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" src={imgUrl} alt={component?.customName || component?.name} />
                    ) : (
                      <Icon name="Package" className="w-12 h-12 text-muted-foreground/30" />
                    )}
                  </div>

                  {/* Card Details */}
                  <div className="p-3 flex-1 flex flex-col justify-between space-y-2">
                    <div>
                      <h4 className="font-bold text-xs text-slate-800 line-clamp-1 mb-1" title={component?.customName || component?.name}>{component?.customName || component?.name}</h4>
                      <p className="text-[9px] text-slate-500 font-medium font-mono">SKU: <span className="font-semibold text-gray-700 font-sans">{component?.manufacturerPartNumber || 'N/A'}</span></p>
                      <p className="text-[9px] text-slate-500 font-medium">Product: <span className="font-semibold text-gray-700">{component?.product?.name || 'N/A'}</span></p>
                    </div>

                    <div className="flex justify-between items-center bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                      <div>
                        <p className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Price</p>
                        <p className="font-black text-[11px] text-blue-600">{formatPrice(component?.price || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Stock</p>
                        <p className={`font-black text-[11px] ${component?.stockQuantity > 0 ? 'text-green-600' : 'text-red-500'}`}>{component?.stockQuantity || 0} units</p>
                      </div>
                    </div>

                    {/* Status Check Switch */}
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <Switch 
                          checked={component?.isActive}
                          onCheckedChange={() => handleToggleStatus(component)}
                          disabled={isCustomOrderingActive}
                        />
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${component?.isActive ? 'text-green-600' : 'text-slate-400'}`}>
                          {component?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="flex items-center gap-1">
                        <button 
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleEdit(component); }}
                          disabled={isCustomOrderingActive}
                          className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border hover:text-blue-600 transition-colors"
                          title="Edit Component"
                        >
                          <Icon name="Edit" size={14} />
                        </button>
                        <button 
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDelete(component.id); }}
                          disabled={isCustomOrderingActive}
                          className="p-1.5 bg-gray-50 hover:bg-red-50 text-red-500 rounded border border-red-100 hover:text-red-700 hover:border-red-200 transition-colors"
                          title="Delete Component"
                        >
                          <Icon name="Trash" size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {isCustomOrderingActive && <th className="w-10 px-4 py-3 bg-gray-100"></th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("component")}>Component {sortConfig.key === "component" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("condition")}>Condition {sortConfig.key === "condition" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("sku")}>SKU {sortConfig.key === "sku" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("upc")}>UPC {sortConfig.key === "upc" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("manufacturer")}>Manufacturer {sortConfig.key === "manufacturer" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("product")}>Product {sortConfig.key === "product" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("price")}>Price {sortConfig.key === "price" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("return")}>Return Period {sortConfig.key === "return" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("stock")}>QTY {sortConfig.key === "stock" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("views")}>Views {sortConfig.key === "views" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("status")}>Status {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-12 text-center text-gray-500">
                      <Icon name="Package" className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>Loading your components...</p>
                    </td>
                  </tr>
                ) : filteredComponents?.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-12 text-center text-gray-500">
                      <Icon name="Package" className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>No components found for your account</p>
                    </td>
                  </tr>
                ) : (
                  displayComponentsList?.map((component, index) => (
                    <tr 
                      key={component?.id} 
                      draggable={isCustomOrderingActive}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${isCustomOrderingActive ? 'bg-blue-50/20' : ''}`}
                      onClick={() => {
                        if (!isCustomOrderingActive) {
                          setSelectedComponent(component);
                          setShowPreviewModal(true);
                        }
                      }}
                    >
                      {isCustomOrderingActive && (
                        <td className="px-4 py-4 text-center cursor-move text-gray-400 hover:text-gray-600 transition-colors" onClick={(e) => e.stopPropagation()}>
                          <Icon name="GripVertical" size={16} />
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            {component?.imageUrls?.[0] ? (
                              <img
                                src={component.imageUrls[0].startsWith('http') || component.imageUrls[0].startsWith('/assets') || component.imageUrls[0].startsWith('/api') ? component.imageUrls[0] : imageUploadService.getPublicUrl(component.imageUrls[0], 'component-images')}
                                alt={`Thumbnail for ${component?.customName || component?.name}`}
                                className="h-10 w-10 rounded-full object-cover border border-gray-200"
                              />
                            ) : (
                              <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                                <Package className="w-5 h-5 text-gray-500" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{component?.customName || component?.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {component?.condition || 'New'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">                      {component?.manufacturerPartNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {component?.upc}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {component?.manufacturer?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {component?.product?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {formatPrice(component?.price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {component?.returnperiod !== null && component?.returnperiod !== undefined ? `${component.returnperiod} Days` : 'None'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          component?.stockQuantity === 0 
                            ? 'bg-red-100 text-red-800'
                            : component?.stockQuantity < 10
                            ? 'bg-yellow-100 text-yellow-800' :'bg-green-100 text-green-800'
                        }`}>
                          {component?.stockQuantity || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold text-slate-700">
                        {component?.viewsCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                           <span className="text-xs text-gray-500 w-12 text-right">{component?.isActive ? 'Active' : 'Inactive'}</span>
                           <Switch 
                              checked={component?.isActive} 
                              onCheckedChange={() => handleToggleStatus(component)} 
                           />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(component); }}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Icon name="Edit" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(component?.id); }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Icon name="Trash" className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Pagination Controls */}
      {filteredComponents.length > itemsPerPage && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-lg shadow">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredComponents.length)}</span> of{' '}
                <span className="font-medium">{filteredComponents.length}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <Icon name="ChevronLeft" className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <Icon name="ChevronRight" className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Component Preview Modal */}
      {showPreviewModal && selectedComponent && (
        <ComponentDetailModal
            component={selectedComponent}
            isOpen={true}
            onClose={() => { setShowPreviewModal(false); setSelectedComponent(null); }}
            onAddToCart={() => {}}
            isAuthenticated={true}
            onAuthRequired={() => {}}
            preview={true}
        />
      )}
      </div>

      {/* SECTION 2: Your Bundles */}
      <div className="pt-6 border-t border-gray-200">
        <SellerBundleManagement sellerId={sellerAccountId} />
      </div>

      {/* Modal - Re-use existing ComponentModal, ensure it handles createdBy for new components */}
      {showModal && (
        <ComponentModal
          component={selectedComponent}
          onClose={handleModalClose}
          currentUserId={user?.id}
          sellerAccountId={sellerAccountId} // Pass the seller account ID
          selectedProductId={selectedProductId} // Pass the selected product ID
        />
      )}
      {/* No Ratings Modal for Seller Dashboard initially */}
      {/* {showRatingsModal && ( ... )} */}
    </div>
  );
}
